/** @jsx jsx */

import React, { Fragment as _, useState, useRef, useEffect } from 'react';
import { css, jsx } from '@emotion/core';
import setBlockProps from 'services/apiHelpers/setBlockProps';
import { colors } from 'styles/variables';
import { findByKeyVal_V2 } from 'services/findByKeyVal';
import { addHotSpotOption } from 'factories/interactions/hotspot_isolated';
import { setBlocksAction } from 'actions/setBlocksAction';
import setBlock from 'services/apiHelpers/setBlockProps';
import getStore from 'services/getStore';
import getBlockById from 'services/apiHelpers/getBlockById';
import HotspotControlBtn from './HotspotControlBtn';
import EditorBtn from 'components/UI/EditorBtn';
import { Block } from '@chameleoncreator/creator-app';
import Hotspot_Add from 'assets/Hotspot_Add.svg';
import Hotspot_Done from 'assets/Hotspot_Done.svg';
import imageUploadError from 'assets/imageUploadError_hotSpot.svg';
import BigSwitch from 'components/UI/BigSwitch';
import { H1 as H1_v2 } from '../EditorUtilities/UI/Headings';
import TogglePanel from '../EditorUtilities/UI/TogglePanel';
import CheckBox_v2 from '../EditorUtilities/UI/CheckBox';
import NumberPicker_v2 from '../EditorUtilities/UI/NumberPicker';
import ColorPicker_v2 from '../EditorUtilities/UI/ColorPicker';
import Info from '../EditorUtilities/UI/Info';
import { ImageLibraryContext } from '../EditImage/EditImage';
import ImageLibrary from '../EditImage/ImageLibrary';
import pubSub from 'services/pubSub';
import setNewBlockNewTree from 'services/apiHelpers/setNewBlockNewTree';
import {
  removeHotSpotOption,
  getNextHotSpotBlock,
} from 'factories/interactions/hotspot_isolated';
import { getPolygonCenter } from './getPolygonCenter';
import { Circle, Rectangle, Freeform, HotSpotTypeRow } from './HotSpotTypes';
import { Panel } from '../EditorUtilities/UI/Panel';
import { Input } from '../EditorUtilities/UI/Input';
import { useScoringQuizzes } from 'services/apiHelpers/useScoringQuizzes';
import { SingleColumnElement } from '../EditorUtilities/grid';
import Button from '../EditorUtilities/UI/Button';
// import CorrectAnswersWidget from '../EditAssessmentQuestion/CorrectAnswersWidget';
// import ModuleNavigationModal from 'components/Editor/ModuleGlobalNavigation/ModuleGlobalNavigationModal';
import { NavigationSettings } from 'components/NavigationSettings/NavigationSettings_v2';
import { NavigationSettingsButton } from 'components/NavigationSettings/NavigationSettingsButton';
// import { NavigationWidget } from 'components/NavigationSettings/NavigationWidget';
// import { moduleNavigationActionsList } from 'components/NavigationSettings/ModuleNavigation';
import PlusIcon from 'assets/Plus_Icon.svg';
import { isObject } from 'services/isObject';

const store = getStore();
const { lPink, lGrey0, lGrey3, mint } = colors;

const togglePanelsCache = {};

export const UPDATE_RECTANGLE_HOTSPOT_LOCAL_STATE =
  'UPDATE_RECTANGLE_HOTSPOT_LOCAL_STATE';

const isEmpty = (obj: any[] | { [key: string]: any }): boolean => {
  return !!!(Array.isArray(obj) ? obj : Object.values(obj)).length;
};

const setUpdated = () => {
  return new Date().toString();
};

const EditHotSpot = ({ block }: { block?: any }) => {
  const {
    image,
    src,
    imageId = undefined,
    hotspotType,
    hotSpotColor,
    hotSpotSize,
    hotSpotsAnimated = true,
    hotSpotsVisible = true,
    rectangleBorderRadius = 0,
    rectangleDisplayOutlineOnly = false,
    useMediaInFeedback = true,
    showSeenState = true,
    description = undefined,
  } = block.props;

  const { id: hotspotsBlockId, children } = block;

  const [imageLibrary, setImageLibrary] = useState(false);

  const [modal, showModal] = useState(false);

  const [hotspotNavigationId, setHotspotNavigationId] = useState(undefined);

  // current 'active' hotSpot
  const currentActiveHotspotWithNav = getBlockById(hotspotNavigationId);
  //
  let linkData = {
    url: '',
    title: '',
    target: '',
  };

  if (currentActiveHotspotWithNav) {
    const currentActiveHotspotWithNavProps = currentActiveHotspotWithNav.props;
    //
    if (currentActiveHotspotWithNavProps) {
      const navigation = currentActiveHotspotWithNavProps.navigation;
      // we'll put nav on linkData early if it exists ( means it's a super old build )
      // overwrite later if we have the updated linkData
      if (navigation) {
        linkData.url = navigation;
      }

      const savedLinkData = currentActiveHotspotWithNavProps.linkData;

      // if we have savedLinkData && it is indeed a plain object that can be destructured
      // that second check is just being superstitious though at this point
      if (savedLinkData && isObject(savedLinkData)) {
        const { url, title, target } = savedLinkData;

        linkData = {
          url: url || navigation || '',
          title: title || '',
          target: target || '_self',
        };
      }
    }
  }

  // this is our actual interaction ' root '
  const hotSpotIsolatedParent = (Object.values(
    getStore().getState().blocks
  ).filter((b: any) => b.meta && b.meta.subType === 'HotSpot') as any).filter(
    (c: any) =>
      findByKeyVal_V2(c.id, 'type', 'HotSpotIsolated')!.id === hotspotsBlockId
  )[0];

  const hotSpotScorable = findByKeyVal_V2(
    hotSpotIsolatedParent.id,
    'type',
    'HotSpotScorable'
  ) as any;

  const {
    lockDown,
    score,
    immediateFeedback = true,
    isGlobalNavigation = false,
  } = hotSpotIsolatedParent.props;

  // we'll put these specifically on the scorable component if it exists
  const {
    correctAnswers = [],
    numberOfAttempts = 1,
    showAttemptNumber = true,
    showCorrectAnswer = true,
    navigationBehaviour = {
      correct: false,
      incorrect: false,
    },
  } = hotSpotScorable?.props || {};

  let useMediaInHotSpotScorableFeedback =
    hotSpotScorable?.props?.useMediaInFeedback;
  if (useMediaInHotSpotScorableFeedback === undefined) {
    useMediaInHotSpotScorableFeedback = true;
  }

  const [currentHotspotId, setCurrentHotspotId] = useState<number | undefined>(
    undefined
  );

  const scoringQuizzes = useScoringQuizzes();

  const currentBlock = useRef<Block>(block);
  currentBlock.current = block;

  const scaleFactors = useRef<{
    scale: number;
    inverseScale: number;
  }>({
    scale: 1,
    inverseScale: 1,
  });

  const { scale, inverseScale } = scaleFactors.current;

  const currentHotspotBlock = useRef<Block | null>(null);
  currentHotspotBlock.current = currentHotspotId
    ? getBlockById(currentHotspotId)
    : null;

  const saveTogglePanelState = (panel, open) => {
    togglePanelsCache[hotspotsBlockId] = {
      ...togglePanelsCache[hotspotsBlockId],
      [panel]: open,
    };

    setBlockProps(block, { ...block.props });
  };

  const setHotspotType = shape => {
    const prevHotspotType = hotspotType;

    const [newBlock, newTree] = getNextHotSpotBlock(block)!;

    const inverseScaled = value => {
      return value * inverseScale;
    };

    if (newBlock && newBlock.props) {
      (newBlock.children as Block[]).forEach(child => {
        const childBlock: Block = getBlockById(child.id);
        const points: any[] | any[][] = childBlock?.props?.points;

        let newPoints: any = [];

        if (prevHotspotType === 'circle') {
          //
          if (shape === 'polygon') {
            newPoints = [
              [points[0], points[1] - inverseScaled(25)],
              [points[0] + inverseScaled(25), points[1]],
              [points[0], points[1] + inverseScaled(25)],
              [points[0] - inverseScaled(25), points[1]],
            ];
          }

          if (shape === 'rectangle') {
            newPoints = {
              x: points[0] - inverseScaled(30),
              y: points[1] - inverseScaled(20),
              w: inverseScaled(60),
              h: inverseScaled(40),
            };
          }
        }

        if (prevHotspotType === 'rectangle') {
          //
          const points_ = { ...points } as any;
          const center = [points_.x + points_.w / 2, points_.y + points_.h / 2];

          if (shape === 'polygon') {
            newPoints = [
              [center[0], center[1] - inverseScaled(25)],
              [center[0] + inverseScaled(25), center[1]],
              [center[0], center[1] + inverseScaled(25)],
              [center[0] - inverseScaled(25), center[1]],
            ];
          }

          if (shape === 'circle') {
            const points_ = { ...points } as any;
            newPoints = [points_.x + points_.w / 2, points_.y + points_.h / 2];
          }
        }

        if (prevHotspotType === 'polygon') {
          //
          if (shape === 'circle') {
            newPoints = getPolygonCenter(points);
          }

          if (shape === 'rectangle') {
            const center = getPolygonCenter(points);

            newPoints = {
              x: center[0] - inverseScaled(30),
              y: center[1] - inverseScaled(20),
              w: inverseScaled(60),
              h: inverseScaled(40),
            };
          }
        }

        if (childBlock.props) {
          childBlock.props.shape = shape;
          childBlock.props.points = newPoints;
          // this was broken : setUpdated;
          childBlock.props.updated = setUpdated();
        }
      });

      newBlock.props.hotspotType = shape;
    }

    setCurrentHotspotId(undefined);
    setNewBlockNewTree(newTree, newBlock, store, setBlocksAction);
  };

  const createNew = e => {
    const hotSpotId = `#${Math.random()
      .toString(16)
      .substr(2, 6)}`;

    const [newBlock, newTree] = addHotSpotOption(block, block.children.length, {
      shape: hotspotType,
      name: `Hotspot ${hotSpotId}`,
      points: hotspotType === 'rectangle' ? {} : [],
      updated: setUpdated(),
    });

    if (newBlock === block) {
      return;
    }

    const store = getStore();

    setNewBlockNewTree(newTree, newBlock, store, setBlocksAction);

    if (newBlock && newBlock.children) {
      const lastChild = newBlock.children[newBlock.children.length - 1] as any;
      setCurrentHotspotId(lastChild?.id);
    }
  };

  const remove = (hotSpotBlock, index) => {
    const [newBlock, newTree] = removeHotSpotOption(hotSpotBlock, index);

    if (newBlock === hotSpotBlock) {
      return;
    }

    setNewBlockNewTree(newTree, newBlock, store, setBlocksAction);
  };

  const showFeedback = (feedbackFor: 'correct' | 'incorrect' | 'attempt') => {
    pubSub.publish(`SHOW_HOTSPOT_FEEDBACK_${hotSpotIsolatedParent.id}`, {
      result: feedbackFor,
    });
  };

  useEffect(() => {
    return () => {
      if (currentHotspotBlock.current) {
        currentHotspotBlock.current = null;
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      // remove any hotspots that have no points
      const [newBlock, newTree] = getNextHotSpotBlock(currentBlock.current)!;

      const oldBlockHasChildren =
        currentBlock.current && Array.isArray(currentBlock.current.children);
      const newBlockHasChildren =
        newBlock.children && Array.isArray(newBlock.children);
      if (oldBlockHasChildren && newBlockHasChildren) {
        newBlock.children = (currentBlock.current.children as Block[]).filter(
          child => {
            const hotspot = getBlockById(child.id) as any;

            const { name, points } = hotspot?.props;
            //
            return !isEmpty(points);
          }
        );

        setNewBlockNewTree(newTree, newBlock, store, setBlocksAction);
      }
    };
  }, []);

  useEffect(() => {
    const pS = pubSub.subscribe(
      'UPDATE_HOTSPOT_POINTS',
      ({ points, updateRectangleHotspotLocalState = undefined }) => {
        //

        if (!currentHotspotBlock.current) {
          return;
        }

        if (hotspotType === 'circle') {
          setBlockProps(currentHotspotBlock.current, {
            points,
            updated: new Date().toString(),
          });
        }

        if (hotspotType === 'polygon') {
          const currentHotspotBlockPoints =
            currentHotspotBlock.current?.props?.points;

          setBlockProps(currentHotspotBlock.current, {
            points: [...currentHotspotBlockPoints, points],
            updated: setUpdated(),
          });
        }

        if (hotspotType === 'rectangle') {
          if (updateRectangleHotspotLocalState) {
            pubSub.publish(
              `${UPDATE_RECTANGLE_HOTSPOT_LOCAL_STATE}_hotspot-${currentHotspotBlock.current.id}`,
              points
            );

            return;
          }

          setBlockProps(currentHotspotBlock.current, {
            points,
            updated: setUpdated(),
          });
        }
      }
    );

    return () => {
      pS();
    };
  }, [currentHotspotBlock.current, hotspotType]);

  useEffect(() => {
    const pS = pubSub.subscribe('UPDATE_HOTSPOT_SCALE_FACTORS', $ => {
      scaleFactors.current = $;
      //
    });

    return () => {
      pS();
    };
  }, [src]);

  useEffect(() => {
    const pS = pubSub.subscribe(
      `OPEN_GLOBAL_NAVIGATION_${hotSpotIsolatedParent.id}`,
      ({ id }) => {
        setHotspotNavigationId(id);
        requestAnimationFrame(() => {
          showModal(true);
        });
      }
    );

    return pS;
  }, []);

  return (
    <_>
      <TogglePanel
        label={<H1_v2>interaction functions & behaviours</H1_v2>}
        toggle={
          togglePanelsCache[hotspotsBlockId]
            ? togglePanelsCache[hotspotsBlockId]['interaction: settings']
            : true
        }
        onToggle={open => saveTogglePanelState('interaction: settings', open)}
      >
        <CheckBox_v2
          label="Lock down"
          on={!!lockDown}
          onClick={() => {
            setBlockProps(hotSpotIsolatedParent, {
              lockDown: !!lockDown ? 0 : 1,
            });
          }}
        />

        {hotSpotScorable && !immediateFeedback && (
          <div
            style={{
              ...(scoringQuizzes.length && {
                opacity: 0.5,
                pointerEvents: 'none',
              }),
            }}
          >
            {!!scoringQuizzes.length && (
              <div
                style={{
                  margin: '0 0 5px 0',
                  fontSize: 12,
                  fontWeight: 500,
                  color: 'red',
                }}
              >
                Scoring is disabled for this interaction while an assessment is
                scoring.
              </div>
            )}
            <CheckBox_v2
              label="Score"
              on={!!score}
              onClick={() =>
                setBlockProps(hotSpotIsolatedParent, { score: !score })
              }
            />
          </div>
        )}

        {/* was score */}
        {!immediateFeedback && (
          <_>
            <div
              style={{
                display: 'flex',
                width: '100%',
                margin: '10px 0 15px 0',
              }}
            >
              <Button
                enabled={true}
                onClick={e => {
                  showFeedback('correct');
                }}
                style={{
                  margin: '0 5px 0 0',
                }}
              >
                Show correct
              </Button>

              <Button
                enabled={true}
                onClick={e => {
                  showFeedback('incorrect');
                }}
                style={{
                  margin: '0 0 0 5px',
                }}
              >
                Show incorrect
              </Button>
            </div>
            <div
              css={css({
                margin: '10px 0 0 0',
              })}
            >
              <NumberPicker_v2
                label="Number of attempts"
                min={1}
                value={numberOfAttempts}
                onChange={value => {
                  setBlockProps(hotSpotScorable, { numberOfAttempts: value });
                }}
              />
            </div>
            {numberOfAttempts > 1 && (
              <SingleColumnElement>
                <Button
                  enabled={true}
                  onClick={e => {
                    showFeedback('attempt');
                  }}
                >
                  Show attempt feedback
                </Button>
              </SingleColumnElement>
            )}
            {numberOfAttempts > 1 && (
              <CheckBox_v2
                label="Show attempt number"
                on={showAttemptNumber ? 1 : 0}
                onClick={() =>
                  setBlockProps(hotSpotScorable, {
                    showAttemptNumber: !showAttemptNumber,
                  })
                }
              />
            )}
            <div
              css={css`
                margin: 10px 0 0 0;
              `}
            >
              <CheckBox_v2
                label="Include media in feedback"
                on={useMediaInHotSpotScorableFeedback ? 1 : 0}
                onClick={() =>
                  setBlockProps(hotSpotScorable, {
                    useMediaInFeedback: !useMediaInHotSpotScorableFeedback,
                  })
                }
              />
            </div>
            <div
              css={css`
                margin: 10px 0 0 0;
              `}
            >
              <CheckBox_v2
                label="Show correct answer on completion"
                on={showCorrectAnswer ? 1 : 0}
                onClick={() =>
                  setBlockProps(hotSpotScorable, {
                    showCorrectAnswer: !showCorrectAnswer,
                  })
                }
              />
            </div>

            <div
              style={{
                margin: '15px 0 15px 0',
              }}
            >
              <Info
                info="Control how you would like the interaction to progress when resolved."
                style={{ margin: '0 0 5px 0' }}
              >
                Navigation
              </Info>

              <div>
                <CheckBox_v2
                  label="Remain on page when answered correct"
                  on={navigationBehaviour.correct ? 1 : 0}
                  style={{
                    margin: '0 0 10px 0',
                  }}
                  onClick={() =>
                    setBlockProps(hotSpotScorable, {
                      navigationBehaviour: {
                        ...navigationBehaviour,
                        correct: !navigationBehaviour.correct,
                      },
                    })
                  }
                />

                <CheckBox_v2
                  label="Remain on page when answered incorrect"
                  on={navigationBehaviour.incorrect ? 1 : 0}
                  style={{
                    margin: '0 0 10px 0',
                  }}
                  onClick={() =>
                    setBlockProps(hotSpotScorable, {
                      navigationBehaviour: {
                        ...navigationBehaviour,
                        incorrect: !navigationBehaviour.incorrect,
                      },
                    })
                  }
                />
              </div>
            </div>
          </_>
        )}
      </TogglePanel>

      <TogglePanel
        label={<H1_v2>hotspot styling</H1_v2>}
        toggle={
          togglePanelsCache[hotspotsBlockId]
            ? togglePanelsCache[hotspotsBlockId]['hotspot: styling']
            : true
        }
        onToggle={open => saveTogglePanelState('hotspot: styling', open)}
        hr={false}
      >
        <div
          css={css`
            margin: 15px 0 0 0;
          `}
        >
          <EditorBtn
            label="Insert image"
            style={{
              width: `${'175px' || '100%'}`,
              margin: '0 0 15px 0',
              border: `1px solid ${lGrey0}`,
              background: '#fff',
              color: lGrey0,
              fontSize: 11,

              hover: {
                color: '#fff',
                background: '#28292b',
              },
            }}
            css={css`
              min-height: 30px;
            `}
            onClick={() => {
              setImageLibrary(!imageLibrary);
            }}
          />
        </div>

        <Panel
          title="Image description"
          info="Give your image a description for screen readers."
          css={css`
            font-size: 12px;
          `}
        >
          <Input
            defaultValue={description}
            update={value => {
              setBlock(block, { description: value });
            }}
          />
        </Panel>
        <div
          css={css`
            margin: 10px 0 0 0;
          `}
        >
          <Info
            info={
              <_>
                <div>
                  Select "Circle" to create round hot spots with an optional
                  pulsing animation.
                </div>
                <div>
                  Select "Rectangle" to draw an outline around areas of
                  interest.
                </div>
                <div>
                  Select "Freeform" to draw an outline around areas of interest.
                </div>
              </_>
            }
          >
            Type
          </Info>
        </div>

        <div
          css={css({
            position: 'relative',
            margin: '0 0 0 -15px',
          })}
        >
          <HotSpotTypeRow
            selected={hotspotType === 'circle'}
            label="Circle"
            onClick={() => {
              setHotspotType('circle');
            }}
          >
            <Circle
              color={hotspotType === 'circle' ? hotSpotColor : undefined}
            />
          </HotSpotTypeRow>

          <HotSpotTypeRow
            selected={hotspotType === 'rectangle'}
            label="Rectangle"
            onClick={() => {
              setHotspotType('rectangle');
            }}
          >
            <Rectangle
              color={hotspotType === 'rectangle' ? hotSpotColor : undefined}
            />
          </HotSpotTypeRow>

          <HotSpotTypeRow
            selected={hotspotType === 'polygon'}
            label="Freeform"
            onClick={() => {
              setHotspotType('polygon');
            }}
          >
            <Freeform
              color={hotspotType === 'polygon' ? hotSpotColor : undefined}
            />
          </HotSpotTypeRow>
        </div>

        <ColorPicker_v2
          key="hotSpotColor"
          inColor={hotSpotColor}
          outColor={color => setBlockProps(block, { hotSpotColor: color })}
          label="Color"
        />
        {hotspotType === 'circle' && [
          <NumberPicker_v2
            key="hotSpotSize"
            value={hotSpotSize}
            increment={5}
            onChange={value => {
              setBlockProps(block, { hotSpotSize: value });
            }}
            label="Size"
          />,
          <CheckBox_v2
            key="hotSpotsAnimated"
            label="Include animation"
            on={hotSpotsAnimated}
            onClick={() => {
              setBlockProps(block, { hotSpotsAnimated: !hotSpotsAnimated });
            }}
          />,
        ]}

        {hotspotType === 'rectangle' && (
          <_>
            <NumberPicker_v2
              key="rectangleBorderRadius"
              value={rectangleBorderRadius}
              increment={1}
              onChange={value => {
                setBlockProps(block, { rectangleBorderRadius: value });
              }}
              label="Border radius"
            />

            <CheckBox_v2
              label="Display outline only"
              on={rectangleDisplayOutlineOnly}
              onClick={() => {
                setBlockProps(block, {
                  rectangleDisplayOutlineOnly: !rectangleDisplayOutlineOnly,
                });
              }}
              style={{
                marginTop: 10,
              }}
            />
          </_>
        )}
        <CheckBox_v2
          label="Visibilty"
          on={hotSpotsVisible}
          onClick={() => {
            setBlockProps(block, { hotSpotsVisible: !hotSpotsVisible });
          }}
        />
        {/* !score &&  */}
        {!isGlobalNavigation && (
          <div
            css={css`
              margin: 10px 0 0 0;
            `}
          >
            <CheckBox_v2
              label="Include media in feedback"
              on={useMediaInFeedback ? 1 : 0}
              onClick={() =>
                setBlockProps(block, {
                  useMediaInFeedback: !useMediaInFeedback,
                })
              }
            />
          </div>
        )}

        <div
          css={css`
            margin: 10px 0 0 0;
          `}
        >
          <CheckBox_v2
            label={
              immediateFeedback ? 'Show seen state' : 'Show selected state'
            }
            on={!!showSeenState}
            onClick={() =>
              setBlockProps(block, { showSeenState: !!showSeenState ? 0 : 1 })
            }
          />
        </div>

        {immediateFeedback && (
          <div
            css={css`
              margin: 15px 0 0 0;
            `}
          >
            <Info
              info={
                <_>
                  <div style={{ margin: '0 0 10px 0' }}>
                    Choose between having your content display in a modal window
                    or navigate to another location in your module or an
                    external resource.
                  </div>
                  <div>
                    Once you've created a hotspot, click on it in the module
                    view to add/update content or navigation.
                  </div>
                </_>
              }
              style={{ margin: '0 0 5px 0' }}
            >
              Content type
            </Info>

            <BigSwitch
              leftItem="Modal"
              rightItem="Navigation"
              leftValue="modal"
              rightValue="navigation"
              value={isGlobalNavigation ? 'navigation' : 'modal'}
              callback={value => {
                setBlockProps(hotSpotIsolatedParent, {
                  isGlobalNavigation: value === 'navigation' ? true : false,
                });
              }}
            />
          </div>
        )}

        {(true || image) && (
          <div
            css={css`
              margin: 15px 0 0 0;
              // border: 1px solid ${lGrey3};
              // padding: 5px 0;
            `}
          >
            <div>
              {children.map((child, i) => {
                const { id } = child;
                const hotspot: Block = getBlockById(id);
                const { name, points, navigation } = hotspot?.props as any;

                const editing = currentHotspotId === id;

                return (
                  <HotspotControlBtn
                    key={`HotspotControlBtn_${id}`}
                    hotSpot={child}
                    label={name}
                    type="hoverRemove"
                    color={lGrey0}
                    rotation={45}
                    editing={editing}
                    immediateFeedback={immediateFeedback}
                    isGlobalNavigation={isGlobalNavigation}
                    updateNavigation={() => {
                      return;

                      setHotspotNavigationId(id);
                      requestAnimationFrame(() => {
                        showModal(true);
                      });
                    }}
                    onMouseOver={e => {
                      pubSub.publish(`ON_HOTSPOT_HOVER`, {
                        hotspotId: id,
                        hover: true,
                      });
                    }}
                    onMouseOut={e => {
                      pubSub.publish(`ON_HOTSPOT_HOVER`, {
                        hotspotId: id,
                        hover: false,
                      });
                    }}
                    updateLabel={value => {
                      setBlockProps(hotspot, {
                        name: value,
                        updated: setUpdated(),
                      });
                    }}
                    score={score}
                    isCorrectAnswer={correctAnswers.includes(i)}
                    onUpdateCorrectAnswer={() => {
                      const updatedCorrectAnswers = [...correctAnswers];
                      if (updatedCorrectAnswers.includes(i)) {
                        updatedCorrectAnswers.splice(
                          updatedCorrectAnswers.indexOf(i),
                          1
                        );
                      } else {
                        updatedCorrectAnswers.push(i);
                      }

                      setBlockProps(hotSpotScorable, {
                        correctAnswers: updatedCorrectAnswers.sort(),
                      });
                    }}
                    onReDraw={e => {
                      if (block.props.hotspotType === 'polygon') {
                        // clear the points of this polygon
                        setBlockProps(hotspot, {
                          points: [],
                        });
                      }

                      setCurrentHotspotId(child.id);
                    }}
                    onDone={() => {
                      setCurrentHotspotId(undefined);
                    }}
                    onClick={e => {
                      if (
                        window.confirm(
                          'Are you sure? Deleting a hotspot is an undoable operation and the content associated with it will also be removed.'
                        )
                      )
                        remove(block, i);
                    }}
                    css={css`
                      cursor: pointer;
                      pointer-events: ${currentHotspotId ? 'none' : 'auto'};
                      padding-right: 12px;
                      transition: background 0.2s;
                      background: ${currentHotspotId === id ? mint : ''};

                      ${!editing &&
                        `&:hover {
                          background: #eaefff;
                          _color: ${lPink};
                        }`}
                    `}
                  />
                );
              })}
            </div>

            <div
              css={css`
            display: flex;
              align-items: center;
             width: calc(100% - 10px);
             height: 40px;
             background: url("${
               true || !currentHotspotId
                 ? PlusIcon || Hotspot_Add
                 : Hotspot_Done
             }") no-repeat;
              background-size: 25px 25px;
              background-position: center right;
              padding: 0 10px;

              font-size: 12px;
             color: true || ${!currentHotspotId ? lPink : mint};

             cursor: pointer;

             &:hover {
               opacity: 0.8;
             }

             ${currentHotspotId &&
               `
              opacity: 0.2;
              pointer-events: none;
             `}
           `}
              onClick={e => {
                !currentHotspotId
                  ? createNew(e)
                  : setCurrentHotspotId(undefined);
              }}
            >
              Add new hotspot
            </div>
          </div>
        )}
      </TogglePanel>

      {imageLibrary && (
        <ImageLibraryContext.Provider
          value={{
            onAddImage(result) {
              const url = (result && result.url) || imageUploadError;

              setBlockProps(block, {
                src: url,
                imageId: result.id,
                imgWidth: result.width,
                imgHeight: result.height,
              });
            },
            onClose() {
              setImageLibrary(!imageLibrary);
            },
            currentImageSrc: src || image,
            currentImageId: imageId,
            cacheSelected: {
              yourLibrary: null,
              chameleonsLibrary: 0,
            },
            cacheFilter: {
              value: '',
            },
          }}
        >
          <ImageLibrary />
        </ImageLibraryContext.Provider>
      )}

      <_>
        {modal && (
          <div>
            <NavigationSettings
              id={block.id}
              // noActions
              linkData={linkData}
              tab="module"
              onClose={() => showModal(false)}
            >
              {(localLinkData, updateLocalLinkData, currentTab) => {
                let label = 'Save and close';

                return (
                  <_>
                    <NavigationSettingsButton
                      label="Cancel"
                      onClick={() => {
                        showModal(false);
                      }}
                    />

                    <NavigationSettingsButton
                      label={label}
                      enabled={true}
                      style={{
                        background: mint,
                        color: '#fff',
                        boxShadow:
                          '-16px -16px 27px rgba(255, 255, 255, 0.5), 16px 16px 27px rgba(37, 59, 176, 0.14393)',
                      }}
                      onClick={() => {
                        // double tapping the get of this hotSpot in case we go stale
                        const currentActiveHotspotWithNav = getBlockById(
                          hotspotNavigationId
                        );

                        setBlockProps(currentActiveHotspotWithNav, {
                          linkData: {
                            ...linkData,
                            ...localLinkData,
                          },
                        });

                        showModal(false);
                      }}
                    />
                  </_>
                );
              }}
            </NavigationSettings>
          </div>
        )}
      </_>
    </_>
  );
};

export default EditHotSpot;
